import type { Animal, Feeding, Weight, Settings } from './types';

const DB_NAME = 'foster-pal-db';
const DB_VERSION = 1;

class FosterPalDB {
    private db: IDBDatabase | null = null;
    private initPromise: Promise<void> | null = null;

    async init(): Promise<void> {
        // Prevent multiple simultaneous initializations
        if (this.initPromise) {
            return this.initPromise;
        }

        if (this.db) {
            return Promise.resolve();
        }

        this.initPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error('Failed to open database:', request.error);
                this.initPromise = null;
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // Animals store
                if (!db.objectStoreNames.contains('animals')) {
                    const animalStore = db.createObjectStore('animals', { keyPath: 'id' });
                    animalStore.createIndex('status', 'status', { unique: false });
                    animalStore.createIndex('createdAt', 'createdAt', { unique: false });
                }

                // Feedings store
                if (!db.objectStoreNames.contains('feedings')) {
                    const feedingStore = db.createObjectStore('feedings', { keyPath: 'id' });
                    feedingStore.createIndex('animalId', 'animalId', { unique: false });
                    feedingStore.createIndex('timestamp', 'timestamp', { unique: false });
                }

                // Weights store
                if (!db.objectStoreNames.contains('weights')) {
                    const weightStore = db.createObjectStore('weights', { keyPath: 'id' });
                    weightStore.createIndex('animalId', 'animalId', { unique: false });
                    weightStore.createIndex('timestamp', 'timestamp', { unique: false });
                }

                // Settings store
                if (!db.objectStoreNames.contains('settings')) {
                    db.createObjectStore('settings', { keyPath: 'id' });
                }
            };
        });

        return this.initPromise;
    }

    private async ensureInitialized(): Promise<void> {
        if (!this.db) {
            await this.init();
        }
    }

    private getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): IDBObjectStore {
        if (!this.db) {
            throw new Error('Database not initialized. Call init() first.');
        }
        const transaction = this.db.transaction(storeName, mode);
        return transaction.objectStore(storeName);
    }

    // Animals
    async getAnimals(): Promise<Animal[]> {
        await this.ensureInitialized();
        const store = this.getStore('animals');
        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => {
                console.error('Error getting animals:', request.error);
                reject(request.error);
            };
        });
    }

    async getAnimal(id: string): Promise<Animal | undefined> {
        await this.ensureInitialized();
        const store = this.getStore('animals');
        return new Promise((resolve, reject) => {
            const request = store.get(id);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => {
                console.error('Error getting animal:', request.error);
                reject(request.error);
            };
        });
    }

    async addAnimal(animal: Animal): Promise<void> {
        await this.ensureInitialized();
        const store = this.getStore('animals', 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.add(animal);
            request.onsuccess = () => resolve();
            request.onerror = () => {
                console.error('Error adding animal:', request.error);
                reject(request.error);
            };
        });
    }

    async updateAnimal(animal: Animal): Promise<void> {
        await this.ensureInitialized();
        const store = this.getStore('animals', 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.put(animal);
            request.onsuccess = () => resolve();
            request.onerror = () => {
                console.error('Error updating animal:', request.error);
                reject(request.error);
            };
        });
    }

    async deleteAnimal(id: string): Promise<void> {
        await this.ensureInitialized();
        const store = this.getStore('animals', 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.delete(id);
            request.onsuccess = () => resolve();
            request.onerror = () => {
                console.error('Error deleting animal:', request.error);
                reject(request.error);
            };
        });
    }

    // Feedings
    async getFeedings(animalId?: string): Promise<Feeding[]> {
        await this.ensureInitialized();
        const store = this.getStore('feedings');
        return new Promise((resolve, reject) => {
            let request: IDBRequest;

            if (animalId) {
                const index = store.index('animalId');
                request = index.getAll(animalId);
            } else {
                request = store.getAll();
            }

            request.onsuccess = () => {
                // Sort by timestamp descending
                const feedings = request.result as Feeding[];
                feedings.sort((a, b) => b.timestamp - a.timestamp);
                resolve(feedings);
            };
            request.onerror = () => {
                console.error('Error getting feedings:', request.error);
                reject(request.error);
            };
        });
    }

    async addFeeding(feeding: Feeding): Promise<void> {
        await this.ensureInitialized();
        const store = this.getStore('feedings', 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.add(feeding);
            request.onsuccess = () => resolve();
            request.onerror = () => {
                console.error('Error adding feeding:', request.error);
                reject(request.error);
            };
        });
    }

    // Weights
    async getWeights(animalId?: string): Promise<Weight[]> {
        await this.ensureInitialized();
        const store = this.getStore('weights');
        return new Promise((resolve, reject) => {
            let request: IDBRequest;

            if (animalId) {
                const index = store.index('animalId');
                request = index.getAll(animalId);
            } else {
                request = store.getAll();
            }

            request.onsuccess = () => {
                // Sort by timestamp descending
                const weights = request.result as Weight[];
                weights.sort((a, b) => b.timestamp - a.timestamp);
                resolve(weights);
            };
            request.onerror = () => {
                console.error('Error getting weights:', request.error);
                reject(request.error);
            };
        });
    }

    async addWeight(weight: Weight): Promise<void> {
        await this.ensureInitialized();
        const store = this.getStore('weights', 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.add(weight);
            request.onsuccess = () => resolve();
            request.onerror = () => {
                console.error('Error adding weight:', request.error);
                reject(request.error);
            };
        });
    }

    // Settings
    async getSettings(): Promise<Settings> {
        await this.ensureInitialized();
        const store = this.getStore('settings');
        return new Promise((resolve, reject) => {
            const request = store.get('app-settings');
            request.onsuccess = () => {
                const settings = request.result || { id: 'app-settings', weightUnit: 'grams' as const };
                resolve(settings);
            };
            request.onerror = () => {
                console.error('Error getting settings:', request.error);
                reject(request.error);
            };
        });
    }

    async updateSettings(settings: Settings): Promise<void> {
        await this.ensureInitialized();
        const store = this.getStore('settings', 'readwrite');
        return new Promise((resolve, reject) => {
            const request = store.put({ id: 'app-settings', ...settings });
            request.onsuccess = () => resolve();
            request.onerror = () => {
                console.error('Error updating settings:', request.error);
                reject(request.error);
            };
        });
    }
}

// Singleton instance
export const db = new FosterPalDB();